import { useState, type ReactNode } from 'react';
import type { AgentConversationFile, AgentLibraryFile } from '@forecastify/shared';
import {
  Button,
  Icon,
  SKELETON_WIDTHS,
  Sheet,
  Skeleton,
  Tooltip,
  skeletonWidth,
  type IconName,
} from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { useSession } from '../../session';
import { downloadAttachment, readableFileSize } from '../file-helpers';
import { Lightbox, useObjectUrl } from '../attachments';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C47 · the files somebody handed the Agent: where they went, and what      │
 * │ they have sent before.                                                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Three surfaces over one list (`GET /agent/conversations/:id/files`), and the thing that separates
 * them is one nullable column:
 *
 *  * **`AgentFileChips`** — above the message a file was sent with. `messageId` is that message.
 *  * **`AgentPendingFiles`** — above the composer, holding what will go with the next question.
 *    `messageId` is null.
 *  * **`AgentFileLibrary`** — the browse panel: this thread first, then everything this member has
 *    ever handed the Agent, from any thread.
 *
 * ⚠️⚠️ **THIS IS NOT `attachments.tsx` COPIED, AND THE DIFFERENCE IS THE REQUIREMENT.** C22's list
 * is a stack of 36px rows with a thumbnail, a size, an uploader and two buttons — right beside a
 * comment, wrong inside a transcript, where the file is a footnote to a sentence somebody typed and
 * a full-width row per attachment would out-weigh the question it belongs to. What is shared is the
 * mechanism: `file-helpers.ts` owns the download and the size, so the two lists cannot disagree
 * about either.
 *
 * ⚠️ **An agent attachment is always tabular** — `file.service.ts` refuses anything else on this
 * target — so there is no lightbox here and no `inline` branch. A spreadsheet has no preview, and
 * the icon says which of the two shapes it is.
 */

/**
 * ⚠️ **The glyph is derived from the NAME, not from the MIME type, and that is deliberate here.**
 * `mimeType` is what the sniffer decided, and for spreadsheets it is routinely
 * `application/octet-stream` off a Windows machine — a truthful answer that draws the wrong icon.
 * The extension is what the person sees in their own file manager, so it is what the icon follows.
 */
function glyphFor(filename: string): IconName {
  return /\.(csv|xlsx|xls)$/i.test(filename) ? 'file-spreadsheet' : 'file-text';
}

/**
 * One file, as a chip.
 *
 * ⚠️ **The whole chip is the download**, not a separate icon button beside the name: a chip is 22px
 * tall and a second target inside it is a tap nobody can hit. `title` and the accessible name both
 * say what pressing it does, because *"HSO-monthly.csv"* alone does not.
 */
function FileChip({
  filename,
  byteSize,
  onOpen,
  onRemove,
  removeLabel,
  t,
}: {
  readonly filename: string;
  readonly byteSize: number;
  readonly onOpen: () => void;
  readonly onRemove?: () => void;
  readonly removeLabel?: string;
  readonly t: Translate;
}): ReactNode {
  return (
    <span className="border-input bg-card inline-flex max-w-full items-center gap-1.5 rounded-md border py-1 pr-1 pl-2">
      <Tooltip asChild content={t('agent.files.download', { name: filename })}>
        <button
          type="button"
          onClick={onOpen}
          aria-label={t('agent.files.download', { name: filename })}
          className="flex min-w-0 items-center gap-1.5"
        >
          {/* ⚠️⚠️ **A GLYPH, AND NOT A THUMBNAIL.** This chip is a RECORD of what went with a
            question that has already been asked — there is nothing left to check before pressing
            send, which is the whole job of the square tiles in the composer. Showing the picture
            here too would make the sent strip compete with the pending one for the same glance. */}
          <Icon name={glyphFor(filename)} size={13} />
          <span className="truncate text-[12px]">{filename}</span>
          {/* ⚠️ `tabular-nums` and a fixed shape, so a column of chips does not jitter as sizes differ. */}
          <span className="text-muted-foreground shrink-0 text-[11px] tabular-nums">
            {readableFileSize(byteSize)}
          </span>
        </button>
      </Tooltip>
      {onRemove === undefined ? null : (
        <Tooltip asChild content={removeLabel}>
          <button
            type="button"
            onClick={onRemove}
            aria-label={removeLabel}
            className="text-muted-foreground hover:text-foreground grid size-4 shrink-0 place-items-center rounded"
          >
            <Icon name="x" size={11} />
          </button>
        </Tooltip>
      )}
    </span>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE FILES THAT WENT WITH ONE MESSAGE, ABOVE IT.                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **C43 ARGUED AGAINST EXACTLY THIS AND THE ARGUMENT NEEDED ANSWERING RATHER THAN IGNORING.**
 * `agent-screen.tsx` said a chip under the message a file arrived with *"would say something
 * narrower than the truth"*, because the model is told about every file in the thread on every
 * question. That is still true, and it is why this chip is a **record of when the file arrived**
 * rather than a statement of what the Agent can currently see — and why the panel's *this
 * conversation* section, which lists all of them, is the surface that answers *"what can it see"*.
 * Two different questions; before C47 the screen could only answer the second.
 *
 * ⚠️ **Right-aligned with the person's own bubble**, because these are theirs: a left-aligned strip
 * above a right-aligned pill reads as the Agent having produced the file.
 */
export function AgentFileChips({
  files,
}: {
  readonly files: readonly AgentConversationFile[];
}): ReactNode {
  const t = useT();
  const session = useSession();
  if (files.length === 0) return null;

  return (
    <ul className="flex max-w-[80%] flex-wrap justify-end gap-1.5">
      {files.map((file) => (
        <li key={file.id} className="min-w-0">
          <FileChip
            filename={file.filename}
            byteSize={file.byteSize}
            onOpen={() => void downloadAttachment(session, file)}
            t={t}
          />
        </li>
      ))}
    </ul>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ WHAT WILL GO WITH THE NEXT QUESTION. Above the composer.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS REPLACED A STRIP THAT LISTED THE WHOLE THREAD'S FILES, AND THE REPLACEMENT IS THE
 * POINT.** C43 put `Attachments` here, so a file stayed above the composer for the rest of the
 * conversation — which was honest about what the Agent could see and useless as an answer to *"what
 * am I about to send"*: after four questions the strip was four chips, none of them new. Now the
 * chip moves: it sits here until the question is asked, then above the question. The thread-wide
 * list did not disappear, it moved into the panel.
 *
 * ⚠️ **The sentence above the chips is not decoration.** A file that vanishes from this strip the
 * moment somebody presses Enter needs to have said where it was going first, or its disappearance
 * reads as the upload having been lost.
 */
export function AgentPendingFiles({
  files,
  onRemove,
  busy,
}: {
  readonly files: readonly AgentConversationFile[];
  readonly onRemove: (id: string) => void;
  /** ⚠️ A turn is in flight: the files are already committed to it, so removing one is not offered. */
  readonly busy: boolean;
}): ReactNode {
  const t = useT();
  /* ⚠️ No `useSession` here any more — the strip downloaded nothing once the tiles stopped
     offering it, and a session read for a call that is gone is the shape of a stale refactor. */
  /**
   * ⚠️⚠️ **THE OVERLAY BELONGS TO THE STRIP, NOT TO A CARD.** One at a time is the whole
   * behaviour — a card that owned its own would let two open at once, and closing one would leave
   * the other's scrim underneath. `attachments.tsx` holds it the same way for the same reason.
   */
  const [preview, setPreview] = useState<AgentConversationFile | null>(null);
  if (files.length === 0) return null;

  /**
   * ⚠️⚠️ **INSIDE THE COMPOSER NOW, NOT ABOVE IT — the ruling of 20 Sep.** A strip floating
   * over the box read as a separate thing that had happened; sitting within the same border, above
   * the question being typed, it reads as part of what is about to be sent. It is where every chat
   * product a person has used puts it.
   *
   * ⚠️ **The caption went with the move.** *"Ezekkel a fájlokkal megy a következő kérdés"* was
   * there to say where a chip was going, which a chip outside the box could not show. Inside it,
   * the position says it, and the sentence became the narration the product owner has been
   * striking all day.
   */
  return (
    <div className="mb-2 flex flex-col gap-1.5">
      <ul className="flex flex-wrap gap-2">
        {files.map((file) => (
          <li key={file.id}>
            <PendingCard
              file={file}
              /**
               * ⚠️⚠️ **A PICTURE OPENS. NOTHING ELSE DOES ANYTHING, AND THAT IS THE POINT.**
               * Clicking any tile used to fetch the file to disk — a file the person had chosen off
               * that same disk a moment earlier. *"We don't need this at all"*, 20 Sep.
               *
               * ⚠️ The only question a pending tile answers is *"is this the right one"*: a
               * picture answers it by being shown, and a spreadsheet cannot answer it at all. So a
               * tile without a preview is not a control — see `PendingCard`.
               */
              onOpen={
                file.inline
                  ? () => {
                      setPreview(file);
                    }
                  : undefined
              }
              onRemove={
                busy
                  ? undefined
                  : () => {
                      onRemove(file.id);
                    }
              }
              t={t}
            />
          </li>
        ))}
      </ul>

      {preview === null ? null : (
        <Lightbox
          attachment={preview}
          onClose={() => {
            setPreview(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * ┌──────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ A SQUARE, BECAUSE A PICTURE IS WHAT IT MOSTLY HOLDS — 20 Sep.           │
 * └──────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE FIRST ATTEMPT PUT A 20PX THUMBNAIL IN A TEXT CHIP AND IT SHOWED NOTHING.** A
 * screenshot scaled into the space of an icon is an icon. The product owner asked for the shape
 * every chat composer uses: a tile the image fills, with the filename only where there is no image
 * to show.
 *
 * ⚠️ **A hook cannot be called in a `map`**, which is why this is a component and not a branch
 * inside the strip — `useObjectUrl` fetches one attachment's bytes, so three cards are three
 * components. `use-avatar.ts` records the same rule.
 *
 * ⚠️ **Only what CAN be shown is fetched.** `inline` is the server's own answer to *"may this be
 * rendered in a browser"*, so a spreadsheet's bytes are never pulled down for a tile nobody could
 * look at.
 */
function PendingCard({
  file,
  onOpen,
  onRemove,
  t,
}: {
  readonly file: AgentConversationFile;
  /** ⚠️ Absent for a file with nothing to show — the tile is then not a button at all. */
  readonly onOpen?: () => void;
  readonly onRemove?: () => void;
  readonly t: Translate;
}): ReactNode {
  const preview = useObjectUrl(file.inline ? file : null);
  /* ⚠️ A `button` when it opens something, a plain `div` when it does not — see below. */
  const Body = onOpen === undefined ? 'div' : 'button';
  /* ⚠️ The extension, uppercased — the one word that says what a file without a picture IS. */
  const kind = file.filename.includes('.')
    ? (file.filename.split('.').pop() ?? '').toUpperCase()
    : '';

  return (
    <div className="border-input bg-card relative size-[84px] overflow-hidden rounded-xl border">
      {/* ⚠️⚠️ **A BUTTON ONLY WHERE THERE IS SOMETHING TO PRESS IT FOR.** A tile that looks
          pressable and does nothing is worse than one that plainly does not — and a `disabled`
          button would be a third thing, saying *not right now* about something that will never be
          available. The name says what pressing it does, which is the only action it has. */}
      <Body
        className="size-full text-left"
        {...(onOpen === undefined
          ? {}
          : {
              onClick: onOpen,
              type: 'button' as const,
              title: t('task.attachment.open', { name: file.filename }),
              'aria-label': t('task.attachment.open', { name: file.filename }),
            })}
      >
        {preview === null ? (
          /* ⚠️ The filename and its kind, laid out like the tile an image would fill — so a
             mixed strip is a row of squares rather than squares and chips. */
          <span className="flex size-full flex-col justify-between p-2">
            <span className="line-clamp-2 text-[11px] leading-tight break-all">
              {file.filename}
            </span>
            <span className="text-muted-foreground bg-muted self-start rounded px-1 text-[9.5px] font-medium tracking-wide">
              {kind === '' ? readableFileSize(file.byteSize) : kind}
            </span>
          </span>
        ) : (
          /* ⚠️ `alt=""` — the button around it already names the file, and a screen reader
             reading the name twice is worse than leaving the tile undescribed. */
          <img src={preview} alt="" className="size-full object-cover" />
        )}
      </Body>

      {onRemove === undefined ? null : (
        /* ⚠️⚠️ **OVER THE TILE, NOT BESIDE IT.** The image fills the square, so the × has
           nowhere to sit in the flow — and it carries its own dark disc because it lands on
           whatever pixels the screenshot happens to have there. */
        <Tooltip asChild content={t('agent.files.remove', { name: file.filename })}>
          <button
            type="button"
            onClick={onRemove}
            aria-label={t('agent.files.remove', { name: file.filename })}
            className="bg-foreground/70 text-background hover:bg-foreground absolute top-1 right-1 grid size-5 place-items-center rounded-full"
          >
            <Icon name="x" size={12} />
          </button>
        </Tooltip>
      )}
    </div>
  );
}

/** One row of the panel. Wider than a chip, because here the file is the subject. */
function LibraryRow({
  filename,
  byteSize,
  note,
  onOpen,
  onAttach,
  attaching,
  t,
}: {
  readonly filename: string;
  readonly byteSize: number;
  /** The thread it came from, or the date — whatever the section wants under the name. */
  readonly note: string;
  readonly onOpen: () => void;
  /** Absent in the *this conversation* section: it is already here. */
  readonly onAttach?: () => void;
  readonly attaching: boolean;
  readonly t: Translate;
}): ReactNode {
  return (
    <li className="border-input flex items-center gap-2 rounded-md border p-2">
      <span className="bg-muted text-muted-foreground grid size-8 shrink-0 place-items-center rounded">
        <Icon name={glyphFor(filename)} size={15} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[12.5px]">{filename}</span>
        <span className="text-muted-foreground truncate text-[11px]">
          <span className="tabular-nums">{readableFileSize(byteSize)}</span>
          {note === '' ? '' : ` · ${note}`}
        </span>
      </span>
      <Button
        variant="ghost"
        size="sm"
        onClick={onOpen}
        aria-label={t('agent.files.download', { name: filename })}
        title={t('agent.files.download', { name: filename })}
      >
        <Icon name="download" size={14} />
      </Button>
      {onAttach === undefined ? null : (
        <Button
          variant="outline"
          size="sm"
          onClick={onAttach}
          disabled={attaching}
          aria-disabled={attaching}
          aria-label={t('agent.files.reuse', { name: filename })}
          title={t('agent.files.reuseHint')}
        >
          <Icon name="paperclip" size={13} />
        </Button>
      )}
    </li>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE BROWSE PANEL. Two sections, and the separator is the whole point.│
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"I sent that spreadsheet last month"* is a sentence about a person and not about a thread, so the
 * second section reaches across every conversation this member has had. The first section is this
 * thread — which is also the honest answer to *"what can the Agent see right now"*, the question
 * C43's composer strip used to answer before the pending strip took its place.
 *
 * ⚠️⚠️ **THE SECOND SECTION DELIBERATELY INCLUDES THIS THREAD'S FILES.** It is *all uploads*, not
 * *other uploads*: a member who does not remember which thread they used should not have to know in
 * order to find the file. `attached` is what keeps that from being confusing — a file already in this
 * conversation is shown without the re-attach button, because pressing it would return the row that
 * is already on screen.
 *
 * ⚠️ **A `Sheet` rather than a dropdown**, because a list of a hundred files with two buttons per row
 * is a scroll region, and `dropdown-menu.tsx` is a menu of actions. 420px rather than the design
 * system's 720px default: the content is one narrow column and a sheet twice its width would cover
 * the transcript somebody is reading the filenames against.
 */
export function AgentFileLibrary({
  open,
  onOpenChange,
  thread,
  library,
  loading,
  attaching,
  onAttach,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** This conversation's files — all of them, sent and pending. */
  readonly thread: readonly AgentConversationFile[];
  readonly library: readonly AgentLibraryFile[];
  readonly loading: boolean;
  readonly attaching: boolean;
  readonly onAttach: (fileId: string) => void;
}): ReactNode {
  const t = useT();
  const session = useSession();

  /* ⚠️ By FILENAME and size, not by id: the library's row for a file already in this thread is a
   * DIFFERENT link row over the same blob, so an id comparison would never match. The pair is what
   * the server deduplicated on in substance — one blob — and it is the only shape of that fact the
   * wire carries (a content hash on the wire would be an existence oracle; `0038` argues it). */
  const here = new Set(thread.map((file) => `${file.filename}:${String(file.byteSize)}`));

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={t('agent.files.title')}
      width={420}
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-5 p-4">
        <section className="flex flex-col gap-2">
          <h3 className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
            {t('agent.files.thisThread')}
          </h3>
          {thread.length === 0 ? (
            <p className="text-muted-foreground text-[11.5px]">{t('agent.files.noneHere')}</p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {thread.map((file) => (
                <LibraryRow
                  key={file.id}
                  filename={file.filename}
                  byteSize={file.byteSize}
                  /* ⚠️ Whether it has been asked about yet, which is the one thing this section can
                   * say that the panel's other half cannot. */
                  note={
                    file.messageId === null ? t('agent.files.notSentYet') : t('agent.files.sent')
                  }
                  onOpen={() => void downloadAttachment(session, file)}
                  attaching={attaching}
                  t={t}
                />
              ))}
            </ul>
          )}
        </section>

        {/* ⚠️ A rule and a heading, not a heading alone: the two lists hold the same shape of row and
            the same filenames, and without a visible break the second reads as a continuation of the
            first. */}
        <hr className="border-input" />

        <section className="flex flex-col gap-2">
          <h3 className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
            {t('agent.files.allThreads')}
          </h3>
          {loading ? (
            <>
              <ul aria-hidden className="flex flex-col gap-1.5">
                {SKELETON_WIDTHS.slice(0, 4).map((_widths, index) => (
                  <li
                    key={`skeleton-${String(index)}`}
                    className="border-input flex items-center gap-2 rounded-md border p-2"
                  >
                    <Skeleton className="size-8 shrink-0 rounded" />
                    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                      <Skeleton width={skeletonWidth(index, 0)} />
                      <Skeleton width={skeletonWidth(index, 1) / 2} className="h-2.5" />
                    </div>
                  </li>
                ))}
              </ul>
              <p className="sr-only" role="status">
                {t('common.loading')}
              </p>
            </>
          ) : library.length === 0 ? (
            <p className="text-muted-foreground text-[11.5px]">{t('agent.files.noneAtAll')}</p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {library.map((file) => (
                <LibraryRow
                  key={file.id}
                  filename={file.filename}
                  byteSize={file.byteSize}
                  /* ⚠️ The thread it came from, named — and the untitled case named by the client, for
                   * the reason `agentConversationSchema.title` is nullable rather than defaulted. */
                  note={file.conversationTitle ?? t('agent.untitled')}
                  onOpen={() => void downloadAttachment(session, file)}
                  onAttach={
                    here.has(`${file.filename}:${String(file.byteSize)}`)
                      ? undefined
                      : () => {
                          onAttach(file.id);
                        }
                  }
                  attaching={attaching}
                  t={t}
                />
              ))}
            </ul>
          )}
        </section>
      </div>
    </Sheet>
  );
}
